import React, { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel, formatApiError } from "../lib/format";
import { Card, Loading, EmptyState } from "../components/UI";
import { Download, FileSignature, X } from "lucide-react";
import SignaturePad from "../components/SignaturePad";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function TenantLease() {
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [signing, setSigning] = useState(false);
  const [sigData, setSigData] = useState("");

  const reload = () => api.get("/leases").then(r => {
    setLeases(r.data);
    setActive(r.data[0] || null);
    setLoading(false);
  });
  useEffect(() => { reload(); }, []);

  const sign = async () => {
    if (!sigData) { toast.error("Please sign first"); return; }
    try {
      await api.post(`/leases/${active.id}/sign`, { signature_data_url: sigData, role: "tenant" });
      toast.success("Lease signed");
      setSigning(false); setSigData("");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  if (!active) return <EmptyState icon={FileSignature} title="No lease available" description="Once your manager generates a lease, it will appear here."/>;

  const downloadUrl = `${API_BASE}/leases/${active.id}/download`;
  const canSign = !active.tenant_signature_url && active.lease_status !== "cancelled";

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">My Tenancy Agreement</h1>
        <p className="text-[#5E7166] text-sm">Review your lease, sign it, and download the PDF.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Status</div>
          <div className="mt-2"><span className={statusBadgeClass(active.lease_status)}>{statusLabel(active.lease_status)}</span></div>
        </Card>
        <Card>
          <div className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Period</div>
          <div className="mt-2 text-sm font-semibold">{formatDate(active.lease_start_date)} → {formatDate(active.lease_end_date)}</div>
        </Card>
        <Card>
          <div className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Total Payable</div>
          <div className="mt-2 naira-total text-2xl">{naira(active.total_payable)}</div>
        </Card>
      </div>

      <Card title="Document">
        <div className="flex flex-wrap gap-2 mb-4">
          <a href={downloadUrl} target="_blank" rel="noreferrer" className="btn-outline-emerald"><Download size={14}/> Download PDF</a>
          {canSign && <button onClick={() => setSigning(true)} className="btn-gold" data-testid="tenant-sign-btn"><FileSignature size={14}/> Sign Lease</button>}
          {active.paystack_payment_link && <Link to={`/tenant/payments`} className="btn-gold">Upload Receipt</Link>}
        </div>
        <iframe src={`${downloadUrl}#view=FitH`} className="w-full border border-[#E5EBE8] rounded-lg" style={{height: 700}} title="Lease PDF"/>
      </Card>

      {signing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Sign your lease</h3>
              <button onClick={() => setSigning(false)}><X size={20}/></button>
            </div>
            <p className="text-sm text-[#5E7166] mb-4">By signing, you confirm you have read and agree to the terms.</p>
            <SignaturePad onChange={setSigData}/>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setSigning(false)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={sign} disabled={!sigData} className="btn-gold flex-1 justify-center" data-testid="tenant-confirm-sign">Confirm Signature</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
